import { ATTRIBUTES } from '@shared/attributes/catalog';
import type { ResearchConfigInput } from '@shared/config';
import { useMutation } from '@tanstack/react-query';
import { FileSpreadsheet, Upload } from 'lucide-react';
import { useRef, useState } from 'react';
import { Button } from '../../components/ui/button';
import { Alert, Badge, Spinner } from '../../components/ui/feedback';
import { Select } from '../../components/ui/form';
import { CSRF_HEADER, CSRF_VALUE } from '../../lib/api';
import { errorMessage } from '../../lib/session';

/** Mirrors the UploadSummary the API returns after parsing the file. */
interface UploadSummary {
  id: string;
  fileName: string;
  size: number;
  rowCount: number;
  headers: string[];
  suggestedMapping: Record<string, string>;
  preview: string[][];
  malformedRows: number;
}

/** Attributes a CSV column can be mapped onto, grouped for the picker. */
const MAPPABLE = ATTRIBUTES.filter(
  (attribute) => attribute.layer === 'normalized' || attribute.layer === 'platform',
).map((attribute) => ({ key: attribute.key, label: attribute.label, group: attribute.group }));

export interface SetupCsvProps {
  config: ResearchConfigInput;
  onChange: (patch: Partial<ResearchConfigInput>) => void;
  error?: string | undefined;
}

/** Upload a spreadsheet, then confirm which column means what before anything is imported. */
export function SetupCsv({ config, onChange, error }: SetupCsvProps) {
  const options = config.sourceOptions?.linkedin;
  const [summary, setSummary] = useState<UploadSummary | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData();
      form.append('file', file);
      const response = await fetch('/api/uploads', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { [CSRF_HEADER]: CSRF_VALUE },
        body: form,
      });
      const text = await response.text();
      const payload: unknown = text ? JSON.parse(text) : null;
      if (!response.ok) {
        const body = payload as { error?: { message?: string } } | null;
        throw new Error(body?.error?.message ?? `Upload failed (${String(response.status)}).`);
      }
      return payload as UploadSummary;
    },
    onSuccess: (result) => {
      setSummary(result);
      const value = {
        uploadId: result.id,
        fileName: result.fileName,
        mapping: result.suggestedMapping,
        rowCount: result.rowCount,
      };
      // Written explicitly per source: a computed key would widen the options type.
      onChange({ sourceOptions: { ...config.sourceOptions, linkedin: value } });
    },
  });

  const setMapping = (header: string, attribute: string): void => {
    if (!options) return;
    const value = { ...options, mapping: { ...options.mapping, [header]: attribute } };
    onChange({ sourceOptions: { ...config.sourceOptions, linkedin: value } });
  };

  const mapping = options?.mapping ?? {};
  const mappedCount = Object.values(mapping).filter(Boolean).length;

  return (
    <div className="space-y-4">
      {error ? <Alert tone="error">{error}</Alert> : null}

      <div>
        <input
          ref={fileInput}
          type="file"
          accept=".csv,text/csv"
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) upload.mutate(file);
          }}
        />
        <Button
          variant="outline"
          icon={<Upload className="h-3.5 w-3.5" />}
          loading={upload.isPending}
          onClick={() => fileInput.current?.click()}
        >
          {summary ? 'Choose a different file' : 'Choose your LinkedIn export'}
        </Button>
        <p className="mt-1.5 text-[13px] text-muted-foreground">
          Export from LinkedIn yourself (Sales Navigator list, Connections, or Recruiter), then upload the file here.
          LinkedIn is never crawled. Columns describing a person become People, linked to the company on the same row.
        </p>
      </div>

      {upload.error ? <Alert tone="error">{errorMessage(upload.error)}</Alert> : null}
      {upload.isPending ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Spinner /> Parsing the file…
        </div>
      ) : null}

      {summary ? (
        <>
          <div className="flex flex-wrap items-center gap-2 rounded-[var(--radius-panel)] border border-border bg-muted/40 px-3 py-2">
            <FileSpreadsheet className="h-4 w-4 text-muted-foreground" aria-hidden="true" />
            <span className="text-[13px] font-medium text-foreground">{summary.fileName}</span>
            <span className="tabular text-[13px] text-muted-foreground">{summary.rowCount.toLocaleString()} rows</span>
            <Badge tone={mappedCount > 0 ? 'success' : 'neutral'}>{mappedCount} columns mapped</Badge>
            {summary.malformedRows > 0 ? (
              <Badge tone="warning">{summary.malformedRows} ragged row(s) padded</Badge>
            ) : null}
          </div>

          <div>
            <h3 className="mb-1 text-[13px] font-semibold text-foreground">Map your columns</h3>
            <p className="mb-2 text-[13px] text-muted-foreground">
              One column must be mapped to <strong>Name</strong>. Anything left as “Ignore” is not imported.
            </p>
            <div className="overflow-x-auto rounded-[var(--radius-panel)] border border-border">
              <table className="w-full border-collapse text-[13px]">
                <thead>
                  <tr className="border-b border-border bg-muted/40">
                    <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">Column</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">Sample value</th>
                    <th scope="col" className="px-3 py-2 text-left font-medium text-muted-foreground">Import as</th>
                  </tr>
                </thead>
                <tbody>
                  {summary.headers.map((header, index) => (
                    <tr key={header} className="border-b border-border last:border-b-0">
                      <td className="px-3 py-1.5 font-medium text-foreground">{header}</td>
                      <td className="max-w-56 truncate px-3 py-1.5 text-muted-foreground">
                        {summary.preview[0]?.[index] || <span className="text-muted-foreground">—</span>}
                      </td>
                      <td className="px-3 py-1.5">
                        <Select
                          className="h-8 w-56 text-[13px]"
                          aria-label={`Import "${header}" as`}
                          value={mapping[header] ?? ''}
                          onChange={(event) => setMapping(header, event.target.value)}
                        >
                          <option value="">Ignore this column</option>
                          {MAPPABLE.map((attribute) => (
                            <option key={attribute.key} value={attribute.key}>
                              {attribute.label}
                            </option>
                          ))}
                        </Select>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
